import { Link, NavLink, Outlet, useNavigate } from "react-router";
import { useLogout } from "@platform/hooks";
import { useAuthStore } from "../../stores/authStore";
import { useIdleTimeout } from "../../lib/useIdleTimeout";
import {
  LayoutDashboard, Users, Shield, AppWindow, Settings,
  LogOut, Building2, Activity, ClipboardList,
  Wand2, History, MessageSquare, Mail, FolderOpen, Clock,
  BarChart3, CheckSquare, Layers, Globe2, Database,
} from "lucide-react";
import { cn } from "@nthamizh/ui";
import { isEnterpriseSubdomain, isAppSubdomain, appSubdomainUrl } from "../../lib/subdomain";
import { FeedbackWidget } from "../FeedbackWidget";
import { NotificationBell } from "../NotificationBell";
import { Avatar } from "../shared/Avatar";

const IDLE_TIMEOUT_MS = 15 * 60 * 1000;

interface SidebarItemProps {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  end?: boolean;
}

function SidebarItem({ to, icon: Icon, label, end }: SidebarItemProps) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
          isActive
            ? "bg-indigo-50 text-indigo-700"
            : "text-slate-400 hover:text-white hover:bg-white/10",
        )
      }
    >
      <Icon className="w-4 h-4 flex-shrink-0" />
      {label}
    </NavLink>
  );
}

function SidebarDivider({ label }: { label: string }) {
  return (
    <p className="text-xs text-slate-500 uppercase tracking-wider px-3 py-1.5 font-medium mt-3">
      {label}
    </p>
  );
}

export function EnterpriseAppShell() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);
  const logout = useLogout();
  useIdleTimeout(IDLE_TIMEOUT_MS);

  const handleLogout = async () => {
    try { await logout.mutateAsync(); } finally {
      clear();
      const loginUrl = isEnterpriseSubdomain()
        ? appSubdomainUrl("/login")
        : "/login";
      if (loginUrl.startsWith("http")) {
        window.location.assign(loginUrl);
      } else {
        navigate(loginUrl);
      }
    }
  };

  const isPlatformAdmin = user?.platformRole === "platform_admin";
  const isSupport = user?.platformRole === "support";
  const isPlatformUser = isPlatformAdmin || isSupport;

  // Enterprise admin check — only meaningful on enterprise subdomains
  const isEnterpriseAdmin =
    user?.permissions?.["admin"]?.["manage_enterprise"] === true ||
    user?.roles?.some((r) => r.roleKey === "enterprise_admin") === true;

  // ConfigIQ access — deliberately not gated to any subdomain. Covers
  // platform admins (always full access), enterprise admins, and any
  // user holding a design-module role at whatever scope it was assigned
  // (platform-level for individual users, or enterprise-level). See
  // ConfigIQRoute in routes/AdminRoute.tsx for the matching route guard —
  // this must stay in sync with that check or the link would be shown
  // without a route to back it up, or vice versa.
  const hasDesignRole = user?.roles?.some((r) => r.moduleKey === "design") === true;
  const showConfigIQSection = isPlatformAdmin || isEnterpriseAdmin || hasDesignRole;

  // CostSim visibility — separate check from ConfigIQ (Option B, confirmed).
  // Must stay in sync with CostSimRoute in routes/AdminRoute.tsx.
  const hasCostSimRole = user?.roles?.some((r) => r.moduleKey === "cost_simulator") === true;
  const showCostSimSection = isPlatformAdmin || isEnterpriseAdmin || hasCostSimRole;

  // Granular CostSim page visibility for non-admin users.
  // Platform admins and enterprise admins always see all pages.
  const csPerms = user?.permissions?.["cost_simulator"] ?? {};
  const canCsSimulate    = isPlatformAdmin || isEnterpriseAdmin || !!csPerms["view_simulate"];
  const canCsInteragency = isPlatformAdmin || isEnterpriseAdmin || !!csPerms["view_simulate"] || !!csPerms["view_interagency"];
  const canCsData        = isPlatformAdmin || isEnterpriseAdmin || !!csPerms["manage_data"];

  // Show enterprise section only when:
  // - on an enterprise subdomain AND user has enterprise admin role, OR
  // - platform admin on an enterprise subdomain (managing that enterprise)
  const onEnterpriseSub = isEnterpriseSubdomain();
  const onAppSub = isAppSubdomain();

  const showEnterpriseSection = onEnterpriseSub && (isEnterpriseAdmin || isPlatformAdmin);
  const showPlatformSection = isPlatformUser;

  // Enterprise badge label
  const enterpriseSlug = onEnterpriseSub
    ? window.location.hostname.split(".")[0]
    : null;

  return (
    <div className="min-h-screen flex flex-col bg-ds-bg">
      {/* Header — full width, above both the sidebar and the content, not
          beside the sidebar. Previously this only spanned the content
          column (a flex row with <aside> as the first child, header+main
          as the second) — the sidebar's dark background ran to the very
          top of the viewport with no header over it, while the header
          only started where the sidebar ended. That's the misalignment;
          this is a genuine restructure, not a recolor. Same dark theme
          as the sidebar (#1e1b4b) rather than white, so header and
          sidebar read as one continuous frame instead of two different
          surfaces meeting at a stark seam. */}
      <header className="h-14 flex-shrink-0 flex items-center justify-between px-6 bg-[#1e1b4b]">
        <div className="flex items-center gap-2.5">
          <img src="/logo.png" alt="Tailseed" className="w-6 h-6 object-contain rounded-sm" />
          <span className="font-semibold text-sm tracking-tight text-white">Tailseed</span>
        </div>

        <div className="flex items-center gap-4">
          {isPlatformUser && <NotificationBell />}
          <Link
            to="/profile"
            className="flex items-center gap-2.5 hover:opacity-80 transition-opacity"
          >
            <Avatar name={user?.name ?? "?"} size="sm" />
            <span className="text-sm font-medium text-white">{user?.name}</span>
          </Link>
          <button
            onClick={handleLogout}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-slate-300 hover:bg-white/10 hover:text-white transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </header>

      <div className="flex flex-1 min-h-0">
        {/* Sidebar — navigation only; identity/notifications/sign-out live
            in the header above. Border-top matches the header's own
            background so the two blend into one surface rather than
            showing a visible seam at the corner where they meet. */}
        <aside className="w-52 flex-shrink-0 flex flex-col bg-[#1e1b4b] border-t border-white/10">
          {/* Context badge — enterprise slug or "App" for platform users */}
          <div className="px-3 pt-4 pb-2">
            <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-white/10">
              <Building2 className="w-3.5 h-3.5 text-indigo-300 flex-shrink-0" />
              <span className="text-xs text-indigo-200 font-medium truncate capitalize">
                {enterpriseSlug ?? "App"}
              </span>
            </div>
          </div>

          {/* Nav */}
          <nav className="flex-1 px-3 py-2 space-y-0.5">

            {/* Platform Admin: Platform section, plus ConfigIQ if applicable
                (always true for platform admins — full access is a backend
                bypass, not a role assignment, so this is effectively
                unconditional for this branch, but written via
                showConfigIQSection to stay in sync with the shared check).
                Profile is reached via the header's avatar+name now, not a
                sidebar item — see AppShell's header for that. */}
          {isPlatformUser && onAppSub ? (
            <>
              <SidebarDivider label="Main" />
              <SidebarItem to="/my-files" icon={FolderOpen} label="My files" />

              <SidebarDivider label="Platform" />
              <SidebarItem to="/platform" end icon={LayoutDashboard} label="Dashboard" />
              <SidebarItem to="/platform/enterprises" icon={Building2} label="Enterprises" />
              <SidebarItem to="/platform/users" icon={Users} label="All users" />
              <SidebarItem to="/platform/health" icon={Activity} label="System health" />
              <SidebarItem to="/platform/audit" icon={ClipboardList} label="Audit log" />
              <SidebarItem to="/platform/roles" icon={Shield} label="Role builder" />
              <SidebarItem to="/platform/feedback" icon={MessageSquare} label="Feedback" />
              <SidebarItem to="/platform/email-settings" icon={Mail} label="Email settings" />
              <SidebarItem to="/platform/scheduler" icon={Clock} label="Scheduler" />

              {showConfigIQSection && (
                <>
                  <SidebarDivider label="Design" />
                  <SidebarItem to="/configiq" end icon={Wand2} label="Tasks" />
                  <SidebarItem to="/configiq/drafts" icon={History} label="My tasks" />
                  {isPlatformAdmin && (
                    <SidebarItem to="/configiq/admin" icon={Settings} label="Admin console" />
                  )}
                </>
              )}
              {showCostSimSection && (
                <>
                  <SidebarDivider label="Cost Simulator" />
                  <SidebarItem to="/costsim" end icon={BarChart3} label="Simulator" />
                  {canCsSimulate && (
                    <>
                      <SidebarItem to="/costsim/eligibility"  icon={CheckSquare} label="Eligibility" />
                      <SidebarItem to="/costsim/combinations" icon={Layers}      label="Combinations" />
                    </>
                  )}
                  {canCsInteragency && (
                    <SidebarItem to="/costsim/interagency" icon={Globe2}    label="Interagency" />
                  )}
                  {canCsData && (
                    <SidebarItem to="/costsim/data"        icon={Database}  label="Synced data" />
                  )}
                  {(isPlatformAdmin || isEnterpriseAdmin) && (
                    <SidebarItem to="/costsim/setup" icon={Settings} label="Setup" />
                  )}
                </>
              )}
            </>
          ) : (
            <>
              {/* Enterprise user nav */}
              <SidebarDivider label="Main" />
              <SidebarItem to="/dashboard" end icon={LayoutDashboard} label="Dashboard" />
              <SidebarItem to="/my-files" icon={FolderOpen} label="My files" />

              {showConfigIQSection && (
                <>
                  <SidebarDivider label="Design" />
                  <SidebarItem to="/configiq" end icon={Wand2} label="Tasks" />
                  <SidebarItem to="/configiq/drafts" icon={History} label="My tasks" />
                  {isPlatformAdmin && (
                    <SidebarItem to="/configiq/admin" icon={Settings} label="Admin console" />
                  )}
                </>
              )}

              {showCostSimSection && (
                <>
                  <SidebarDivider label="Cost Simulator" />
                  <SidebarItem to="/costsim" end icon={BarChart3} label="Simulator" />
                  {canCsSimulate && (
                    <>
                      <SidebarItem to="/costsim/eligibility"  icon={CheckSquare} label="Eligibility" />
                      <SidebarItem to="/costsim/combinations" icon={Layers}      label="Combinations" />
                    </>
                  )}
                  {canCsInteragency && (
                    <SidebarItem to="/costsim/interagency" icon={Globe2}    label="Interagency" />
                  )}
                  {canCsData && (
                    <SidebarItem to="/costsim/data"        icon={Database}  label="Synced data" />
                  )}
                  {(isPlatformAdmin || isEnterpriseAdmin) && (
                    <SidebarItem to="/costsim/setup" icon={Settings} label="Setup" />
                  )}
                </>
              )}

              {showEnterpriseSection && (
                <>
                  <SidebarDivider label="Enterprise" />
                  <SidebarItem to="/enterprise/users" icon={Users} label="Users" />
                  <SidebarItem to="/enterprise/roles" icon={Shield} label="Roles" />
                  <SidebarItem to="/enterprise/apps" icon={AppWindow} label="App access" />
                  <SidebarItem to="/enterprise/settings" icon={Settings} label="Settings" />
                  <SidebarItem to="/platform/scheduler" icon={Clock} label="Scheduler" />
                </>
              )}

              {/* Platform admin on enterprise subdomain still gets platform section */}
              {showPlatformSection && onEnterpriseSub && (
                <>
                  <SidebarDivider label="Platform" />
                  <SidebarItem to="/platform" end icon={LayoutDashboard} label="Dashboard" />
                  <SidebarItem to="/platform/enterprises" icon={Building2} label="Enterprises" />
                  <SidebarItem to="/platform/users" icon={Users} label="All users" />
                  <SidebarItem to="/platform/roles" icon={Shield} label="Role builder" />
                  <SidebarItem to="/platform/feedback" icon={MessageSquare} label="Feedback" />
                  <SidebarItem to="/platform/email-settings" icon={Mail} label="Email settings" />
                      <SidebarItem to="/platform/scheduler" icon={Clock} label="Scheduler" />
                </>
              )}
            </>
          )}
        </nav>
      </aside>

        <main className="flex-1 min-w-0 overflow-auto">
          <div className="max-w-5xl mx-auto px-6 py-8">
            <Outlet />
          </div>
        </main>
      </div>
      <FeedbackWidget />
    </div>
  );
}
